import { inject } from '@angular/core'
import {
   ActivatedRouteSnapshot,
   CanActivateFn,
   Router,
   RouterStateSnapshot
} from '@angular/router'
import { MsalGuard } from '@azure/msal-angular'
import { catchError, of, switchMap } from 'rxjs'
import {
   MsalUserGuardRequest,
   MsalUserGuardService
} from '@appShared/services'
import { environment } from '@appEnvironments/environment'


/**
 * Guards the customer-service routes. Requires a successful MSAL sign-in AND
 * the customer-service role (IUser.isCustomerService). Users without that role
 * are redirected away by MsalUserGuardService.
 *
 * Mirrors AuthGuardService, adding the role gate via the `customerServiceRequired`
 * flag on the MsalUserGuardRequest.
 */
export const AuthGuardCustomerCareService: CanActivateFn = (
   route: ActivatedRouteSnapshot,
   state: RouterStateSnapshot
) => {

   const isDevTest = !environment.production
   const msalGuard = inject(MsalGuard)
   const msalGuardService = inject(MsalUserGuardService)
   const router = inject(Router)

   //// IF using multiple guards on route (e.g., [MsalGuard, AuthGuardCustomerCareService])
   //// - then the below method can be used to WAIT on successful result from MsalGuard
   return msalGuard.canActivate(route, state).pipe(
      switchMap((isMsalAuthenticated: boolean) => {
         if (isDevTest) {
            console.log('AuthGuardCustomerCareService:canActivate: isMsalAuthenticated', isMsalAuthenticated, (new Date()).getTime())
         }

         if (!isMsalAuthenticated) {
            return of(false)
         }

         if (isDevTest) {
            console.log('AuthGuardCustomerCareService:canActivate: checking msal-user-guard', (new Date()).getTime())
         }

         return msalGuardService.msalAuthGuard({
            route,
            state,
            customerServiceRequired: true
         } as MsalUserGuardRequest)
      }),
      catchError(() => {
         router.navigate(['/'])
         return of(false)
      })
   )
}
